import '../sentry'
import "@fontsource/nunito/400.css";
import "@fontsource/nunito/400-italic.css";
import './index.css'

import * as Sentry from "@sentry/react"
import { QueryCache, QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import React, { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { routeTree } from '@/app/routeTree.gen'
import { AppFault } from '@/components/app-fault'
import { ApiError } from "@/lib/api";
import { AuthenticationProvider, useAuthentication } from "@/lib/auth/components/auth-provider";
import Client from "@/lib/auth/components/errors/client";
import NotFound from "@/lib/auth/components/errors/not-found";
import { runOidcBootstrap } from "@/lib/auth/oidc-bootstrap";
import DateFilter from "@/lib/filters/date/date-filter";
import {AppJobsLayer} from "@/lib/jobs/app-jobs-layer";
import { UnableToReadTokenError } from '@/lib/types/errors'
import { useDate } from "@/stores/date-filter";
import { useJobsStore } from "@/stores/jobs";
import { useServiceType } from '@/stores/service-type'
import { useToken } from "@/stores/token";

import { PageSkeleton } from "./components/ui/page-skeleton";
import { DEFAULT_QUERY_OPTIONS } from './utils/constants';

import '@/lib/analytics/datadog'
import '@/lib/analytics/mixpanel'

// eslint-disable-next-line prefer-const
let queryClient: QueryClient;

const queryCache: QueryCache = new QueryCache({
  onError: async (error: Error, query): Promise<void> => {
    // Only a genuine auth failure ends the session. Any other status - a 403 from a permissions
    // gap, a 5xx while the api is deploying - is a fault to surface, not a reason to sign the user
    // out. Reacting to every status is what let one api error become a re-authentication loop:
    // /login now redirects straight to Keycloak, which signs the live session back in immediately.
    if (error instanceof ApiError && error.statusCode === 401 && query.queryHash === '["identity"]') {
      // Something failed with auth, you ain't allowed!
      localStorage.clear()

      // But don't spam my error logs.
      queryClient.clear()

      // Git yo raggedy cache outta hea!
      await router.navigate({to: '/login'})
    }
  }
});

queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...DEFAULT_QUERY_OPTIONS,
      throwOnError: (error: Error) => !('statusCode' in error) || (error as ApiError).statusCode === 401
    },
    mutations: {
      throwOnError: (error: Error) => !('statusCode' in error) || (error as ApiError).statusCode === 401
    }
  },
  queryCache: queryCache
})

const router = createRouter({
  routeTree,
  basepath: 'v2',
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  context: {
    date: undefined!,
    identity: undefined!,
    queryClient: undefined!,
    breadcrumbs: undefined!,
    children: undefined!,
    roles: undefined!,
    permissions: undefined!,
    serviceType: undefined!,
    twoFactorInProgress: undefined!,
    canSwitchFirm: undefined!
  }
})

// add type safety to our routes
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function MainApplicationLayer() {
  const { identity } = useAuthentication()

  const { date } = useDate()
  const { token } = useToken()
  const { serviceType } = useServiceType()
  const { permissions } = useToken()
  const qClient = useQueryClient()

  const didMountRef = React.useRef(false)
  const prevIdentityRef = React.useRef<typeof identity>(identity)

  React.useEffect(() => {
    router.invalidate()
  }, [identity, serviceType])

  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      prevIdentityRef.current = identity
      return
    }

    // only clear jobs when identity changes, don't clear on refresh
    if (prevIdentityRef.current !== identity) {
      useJobsStore.getState().clearAllJobs()
      qClient.removeQueries({ queryKey: ["job"] })
      prevIdentityRef.current = identity
    }
  }, [qClient, identity])


  return <RouterProvider
    defaultPendingComponent={PageSkeleton}
    defaultPreload='intent'
    defaultErrorComponent={Client}
    defaultNotFoundComponent={NotFound}
    defaultOnCatch={(error: any) => {

      if ((error.name === 'ApiError' && error.statusCode === 401) || error instanceof UnableToReadTokenError) {
        localStorage.clear()
        window.location.href = `/v2/login?redirect=/`
      }
    }}
    router={router}
    context={{
      date,
      serviceType,
      queryClient: qClient,
      identity,
      breadcrumbs: [
        { label: 'Home', path: '#' },
        { label: 'My Dashboard', path: '/' }
      ],
      children: <DateFilter />,
      roles: token?.roles,
      permissions: permissions,
      canSwitchFirm: true
    }}
  />
}

function App() {
  const [qClient] = React.useState(() => queryClient)

  return (
    <Sentry.ErrorBoundary fallback={<AppFault />} onError={async (error: any) => {
      // Login is only the right destination when the session itself is unusable. Redirecting on
      // every caught error sent any render fault to /login, which bounces a token holder straight
      // back to / and reloads into the same fault - a full-page reload loop for as long as the
      // underlying call keeps failing. A non-auth fault now stops at the fallback instead.
      if (error.name === 'ApiError' && error.statusCode === 401) {
        localStorage.clear()

        window.location.href = `/v2/login?redirect=/`
      }
    }}>
      <QueryClientProvider client={qClient}>
        <AuthenticationProvider>
          <AppJobsLayer>
            <MainApplicationLayer />
          </AppJobsLayer>
        </AuthenticationProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}

const rootElement = document.getElementById('root')!

// This guard is why index.html's boot splash must stay a *sibling* of #root: splash markup
// inside #root would read as existing content and the app would never mount.
if (!rootElement.innerHTML) {
  // The OIDC bootstrap must finish before the router's auth guards run: a `?code` exchange has
  // to have stored its tokens by first render, and a silent-login redirect makes rendering
  // pointless. It resolves immediately on every path that needs no OIDC work.
  void runOidcBootstrap().then(() => {
    const root = createRoot(rootElement, {
      onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
      }),
      onCaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler()
    })

    root.render(
      <StrictMode>
        <App />
      </StrictMode>,
    )
  })
}
